/**
 * NewDealPage — Stage 1: Loan Enquiry
 *
 * Redesigned to match how brokers actually take an enquiry. The old form
 * asked for lender, writing rate, commission, settlement date etc. at
 * intake — values you can't possibly know during a first call.
 *
 * This form ONLY captures what the broker learns in the first 3 minutes:
 *   - who's applying (contact + optional co-applicant)
 *   - what they want (finance type, purpose, asset)
 *   - the numbers they came in with (purchase price, deposit, trade)
 *   - what they said they'd prefer (term, balloon %, repayment frequency)
 *   - where the lead came from + which broker owns it
 *
 * All the post-approval details (rate, commission, settled date, lender
 * commitment) are captured later on the Deal Detail page.
 */
import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  ArrowLeft,
  Loader2,
  User,
  Users,
  Package,
  Wallet,
  Settings2,
  Sparkles,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Combobox } from "@/components/ui/combobox"
import { ContactPicker } from "@/components/ContactPicker"
import {
  contactsApi,
  dealsApi,
  usersApi,
  type DealInput,
  type FinanceType,
  type SellerType,
  type RepaymentFrequency,
} from "@/lib/api"
import { ASSET_TYPES } from "@/pages/deals/asset-types"

const FINANCE_TYPES: { value: FinanceType; label: string }[] = [
  { value: "commercial", label: "Commercial" },
  { value: "consumer", label: "Consumer" },
]

const PURPOSES = [
  { value: "purchase", label: "Purchase" },
  { value: "refinance", label: "Refinance" },
  { value: "working_capital", label: "Working capital" },
  { value: "equipment_upgrade", label: "Equipment upgrade" },
  { value: "consolidation", label: "Debt consolidation" },
  { value: "other", label: "Other" },
]

const SELLER_TYPES: { value: SellerType; label: string }[] = [
  { value: "dealer", label: "Dealer (ABN)" },
  { value: "private", label: "Private seller" },
  { value: "auction", label: "Auction" },
]

const REPAYMENT_FREQUENCIES: { value: RepaymentFrequency; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
]

// Note: canonical list lives on the API at GET /deals/lead-sources so the
// dropdown here + the Deal Detail combobox both draw from the same pool +
// pick up any custom values previously entered by brokers.

export default function NewDealPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()
  const preselectedContactId = searchParams.get("contactId")

  const [form, setForm] = useState<DealInput>({
    contactId: preselectedContactId ?? "",
    financeType: "commercial",
    financeCategory: null,
    loanPurpose: null,
    assetDescription: null,
    sellerType: null,
    dealerName: null,
    purchasePrice: null,
    deposit: null,
    tradeValue: null,
    tradePayout: null,
    amountRequired: null,
    termMonths: null,
    preferredBalloonPct: null,
    preferredRepaymentFrequency: null,
    leadSource: null,
    ownerId: null,
    coApplicantContactId: null,
    notes: null,
    // Enquiries land in the first pipeline stage.
    pipelineStage: "new_lead",
    status: "new_lead",
  })
  const [amountRequiredManual, setAmountRequiredManual] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const brokers = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
  })

  // Lead-source dropdown — fed by GET /deals/lead-sources which returns
  // canonical + previously-used sources sorted by usage count.
  const leadSourcesQ = useQuery({
    queryKey: ["deals-lead-sources"],
    queryFn: () => dealsApi.leadSources(),
    staleTime: 60_000,
  })
  const leadSourceOptions =
    leadSourcesQ.data?.sources.map((s) => ({
      value: s.value,
      label: s.value,
    })) ?? []

  // We fetch the selected contact fully so we can auto-fill owner + lead source.
  const selectedContactQ = useQuery({
    queryKey: ["contact-for-enquiry", form.contactId],
    queryFn: () => contactsApi.get(form.contactId),
    enabled: !!form.contactId,
  })
  const selectedContact = selectedContactQ.data?.contact ?? null

  // Auto-fill broker + lead source from contact when contact changes.
  useEffect(() => {
    if (!selectedContact) return
    setForm((f) => ({
      ...f,
      ownerId: f.ownerId ?? selectedContact.ownerId ?? null,
      leadSource:
        f.leadSource && f.leadSource.trim().length > 0
          ? f.leadSource
          : selectedContact.source ?? null,
    }))
  }, [selectedContact])

  // Amount Required auto-calc (unless broker overrides).
  // Formula: purchasePrice - deposit - tradeValue + tradePayout
  const computedAmountRequired = useMemo(() => {
    const pp = form.purchasePrice ?? 0
    const dp = form.deposit ?? 0
    const tv = form.tradeValue ?? 0
    const tp = form.tradePayout ?? 0
    if (pp === 0 && dp === 0 && tv === 0 && tp === 0) return null
    const result = pp - dp - tv + tp
    return result > 0 ? result : 0
  }, [form.purchasePrice, form.deposit, form.tradeValue, form.tradePayout])

  useEffect(() => {
    if (amountRequiredManual) return
    setForm((f) => ({ ...f, amountRequired: computedAmountRequired }))
  }, [computedAmountRequired, amountRequiredManual])

  const set = <K extends keyof DealInput>(k: K, v: DealInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const create = useMutation({
    mutationFn: (data: DealInput) => dealsApi.create(data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["deals"] })
      qc.invalidateQueries({ queryKey: ["deals-pipeline"] })
      navigate(`/deals/${res.id}`, { replace: true })
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Failed to create enquiry"),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!form.contactId) {
      setError("Contact is required")
      return
    }
    if (!form.financeType) {
      setError("Finance type is required")
      return
    }
    if (!form.financeCategory) {
      setError("Asset type is required")
      return
    }
    // Auto-suggest a deal name from what we know.
    if (!form.dealName) {
      const who =
        selectedContact?.businessName ||
        `${selectedContact?.firstName ?? ""} ${selectedContact?.lastName ?? ""}`.trim() ||
        "Deal"
      form.dealName = `${who} — ${form.financeCategory}`
    }
    create.mutate(form)
  }



  return (
    <AppShell title="New Loan Enquiry">
      <div className="mb-4">
        <Link
          to="/deals"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to deals
        </Link>
      </div>

      <Card className="p-4 mb-4 border-blue-100 bg-blue-50/50">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-4 w-4 flex-none text-blue-600" />
          <p className="text-sm text-blue-900">
            <span className="font-semibold">Stage 1 — Loan Enquiry.</span>{" "}
            Take an enquiry in under 3 minutes. Rate, lender, commission and
            settlement details come later on the deal detail page.
          </p>
        </div>
      </Card>

      {error && (
        <div className="border-destructive/40 bg-destructive/5 text-destructive mb-4 rounded-md border p-3 text-sm">
          {error}
        </div>
      )}

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* ============================================================ */}
          {/* Applicants                                                    */}
          {/* ============================================================ */}
          <Section icon={<User className="h-4 w-4" />} title="Applicants">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="contactId">Contact *</Label>
                <ContactPicker
                  value={form.contactId}
                  onChange={(id) => set("contactId", id)}
                  placeholder="Search by name, business, email or mobile…"
                />
                <p className="text-muted-foreground text-xs mt-1">
                  Can't find them? Type a name and click “Add new contact”.
                </p>
              </div>
              <div>
                <Label htmlFor="coApplicantContactId">Co-applicant</Label>
                <ContactPicker
                  value={form.coApplicantContactId ?? ""}
                  onChange={(id) => set("coApplicantContactId", id || null)}
                  excludeId={form.contactId || undefined}
                  placeholder="Optional — second applicant"
                />
              </div>
            </div>
          </Section>

          {/* ============================================================ */}
          {/* Finance                                                       */}
          {/* ============================================================ */}
          <Section icon={<Package className="h-4 w-4" />} title="Finance & Asset">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="financeType">Finance Type *</Label>
                <select
                  id="financeType"
                  className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                  value={form.financeType ?? "commercial"}
                  onChange={(e) => set("financeType", e.target.value as FinanceType)}
                >
                  {FINANCE_TYPES.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="loanPurpose">Purpose</Label>
                <select
                  id="loanPurpose"
                  className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                  value={form.loanPurpose ?? ""}
                  onChange={(e) => set("loanPurpose", e.target.value || null)}
                >
                  <option value="">Select purpose…</option>
                  {PURPOSES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="financeCategory">Asset Type *</Label>
                <Combobox
                  value={form.financeCategory ?? ""}
                  onChange={(v) => set("financeCategory", v || null)}
                  options={ASSET_TYPES.map((a) => ({ value: a, label: a }))}
                  placeholder="e.g. Car, Truck, Equipment…"
                  emptyText="No matching asset type"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="assetDescription">Asset description</Label>
              <Input
                id="assetDescription"
                value={form.assetDescription ?? ""}
                onChange={(e) => set("assetDescription", e.target.value || null)}
                placeholder="e.g. 2022 Toyota Hilux SR5 Dual Cab, VIN…, rego…"
              />
              <p className="text-muted-foreground text-xs mt-1">
                Year, make/model, variant. Free text — full details captured
                later.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="sellerType">Dealer / private seller</Label>
                <select
                  id="sellerType"
                  className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                  value={form.sellerType ?? ""}
                  onChange={(e) =>
                    set("sellerType", (e.target.value || null) as SellerType | null)
                  }
                >
                  <option value="">Not yet known</option>
                  {SELLER_TYPES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              {form.sellerType === "dealer" && (
                <div>
                  <Label htmlFor="dealerName">Dealer name</Label>
                  <Input
                    id="dealerName"
                    value={form.dealerName ?? ""}
                    onChange={(e) => set("dealerName", e.target.value || null)}
                    placeholder="e.g. Toowoomba Toyota"
                  />
                </div>
              )}
            </div>
          </Section>

          {/* ============================================================ */}
          {/* Money                                                         */}
          {/* ============================================================ */}
          <Section icon={<Wallet className="h-4 w-4" />} title="Numbers">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <MoneyField
                id="purchasePrice"
                label="Purchase Price"
                value={form.purchasePrice}
                onChange={(v) => set("purchasePrice", v)}
              />
              <MoneyField
                id="deposit"
                label="Deposit"
                value={form.deposit}
                onChange={(v) => set("deposit", v)}
              />
              <MoneyField
                id="tradeValue"
                label="Trade Value"
                value={form.tradeValue}
                onChange={(v) => set("tradeValue", v)}
                hint="Value of the vehicle being traded in"
              />
              <MoneyField
                id="tradePayout"
                label="Trade Payout"
                value={form.tradePayout}
                onChange={(v) => set("tradePayout", v)}
                hint="Amount still owing on the trade-in"
              />
            </div>

            <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <Label htmlFor="amountRequired" className="text-blue-900">
                    Amount Required
                  </Label>
                  <p className="text-xs text-blue-800/80">
                    Auto-calculated: Purchase − Deposit − Trade + Payout.
                    Broker can override.
                  </p>
                </div>
                <div className="w-48">
                  <Input
                    id="amountRequired"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={form.amountRequired ?? ""}
                    onChange={(e) => {
                      setAmountRequiredManual(true)
                      const v = e.target.value
                      set(
                        "amountRequired",
                        v === "" ? null : Number(v) || null
                      )
                    }}
                    placeholder="0.00"
                    className="text-right font-medium"
                  />
                </div>
              </div>
              {amountRequiredManual && (
                <button
                  type="button"
                  onClick={() => {
                    setAmountRequiredManual(false)
                    set("amountRequired", computedAmountRequired)
                  }}
                  className="mt-2 text-xs text-blue-700 hover:text-blue-900 underline"
                >
                  Reset to auto-calc
                </button>
              )}
            </div>
          </Section>

          {/* ============================================================ */}
          {/* Preferences                                                   */}
          {/* ============================================================ */}
          <Section icon={<Settings2 className="h-4 w-4" />} title="Client Preferences">
            <p className="text-xs text-muted-foreground -mt-2">
              What the client asked for. Actual terms depend on lender approval.
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="termMonths">Preferred Loan Term</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="termMonths"
                    type="number"
                    min={1}
                    max={120}
                    value={form.termMonths ?? ""}
                    onChange={(e) =>
                      set(
                        "termMonths",
                        e.target.value === "" ? null : Number(e.target.value) || null
                      )
                    }
                    placeholder="60"
                  />
                  <span className="text-muted-foreground text-sm">months</span>
                </div>
              </div>
              <div>
                <Label htmlFor="preferredBalloonPct">Preferred Balloon</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="preferredBalloonPct"
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    value={form.preferredBalloonPct ?? ""}
                    onChange={(e) =>
                      set(
                        "preferredBalloonPct",
                        e.target.value === "" ? null : Number(e.target.value) || null
                      )
                    }
                    placeholder="0"
                  />
                  <span className="text-muted-foreground text-sm">%</span>
                </div>
              </div>
              <div>
                <Label htmlFor="preferredRepaymentFrequency">Repayment Frequency</Label>
                <select
                  id="preferredRepaymentFrequency"
                  className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                  value={form.preferredRepaymentFrequency ?? ""}
                  onChange={(e) =>
                    set(
                      "preferredRepaymentFrequency",
                      (e.target.value || null) as RepaymentFrequency | null
                    )
                  }
                >
                  <option value="">Not specified</option>
                  {REPAYMENT_FREQUENCIES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </Section>

          {/* ============================================================ */}
          {/* Assignment                                                    */}
          {/* ============================================================ */}
          <Section icon={<Users className="h-4 w-4" />} title="Assignment & Source">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="ownerId">Assigned Broker</Label>
                <Combobox
                  value={form.ownerId ?? ""}
                  onChange={(v) => set("ownerId", v || null)}
                  options={(brokers.data?.users ?? []).map((u) => ({
                    value: u.id,
                    label: u.fullName,
                    sublabel: u.email,
                  }))}
                  placeholder="Pick a broker…"
                  emptyText="No brokers found"
                />
              </div>
              <div>
                <Label htmlFor="leadSource">Lead Source</Label>
                <Combobox
                  value={form.leadSource ?? ""}
                  onChange={(v) => set("leadSource", v || null)}
                  options={leadSourceOptions}
                  placeholder="Where did this enquiry come from?"
                  emptyText="Type to add a new source…"
                  creatable
                  createLabel="Add source"
                />
                <p className="text-muted-foreground text-xs mt-1">
                  Most-used sources appear first. Type a new one to add it.
                </p>
              </div>
            </div>
          </Section>

          {/* ============================================================ */}
          {/* Notes                                                         */}
          {/* ============================================================ */}
          <Section title="Notes">
            <Textarea
              value={form.notes ?? ""}
              onChange={(e) => set("notes", e.target.value || null)}
              placeholder="Anything relevant the client mentioned. Bank statements pending, spouse also on title, credit issues to flag, etc."
              rows={4}
            />
          </Section>

          {/* ============================================================ */}
          {/* Submit                                                        */}
          {/* ============================================================ */}
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Link to="/deals">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              disabled={create.isPending || !form.contactId}
            >
              {create.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Creating…
                </>
              ) : (
                "Save enquiry & open deal"
              )}
            </Button>
          </div>
        </form>
      </Card>
    </AppShell>
  )
}

// ────────────────────────────────────────────────────────────────
// Section wrapper — subtle heading + spacing
// ────────────────────────────────────────────────────────────────
function Section({
  icon,
  title,
  children,
}: {
  icon?: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2 pb-1 border-b border-border">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <h3 className="text-foreground text-sm font-semibold">{title}</h3>
      </div>
      {children}
    </section>
  )
}

// ────────────────────────────────────────────────────────────────
// MoneyField — currency input with $ prefix + optional hint
// ────────────────────────────────────────────────────────────────
function MoneyField({
  id,
  label,
  value,
  onChange,
  hint,
}: {
  id: string
  label: string
  value: number | null | undefined
  onChange: (v: number | null) => void
  hint?: string
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
          $
        </span>
        <Input
          id={id}
          type="number"
          step="0.01"
          inputMode="decimal"
          value={value ?? ""}
          onChange={(e) => {
            const v = e.target.value
            onChange(v === "" ? null : Number(v) || null)
          }}
          placeholder="0.00"
          className="pl-6"
        />
      </div>
      {hint && <p className="text-muted-foreground text-xs mt-1">{hint}</p>}
    </div>
  )
}
